import { getMediaQuery } from '../utils/media-query'

/**
 * Main navigation
 */
export class Navigation {
  private body: HTMLElement | null
  private header: Element | null
  private toggle: Element | null
  private navigation: Element | null
  private menu: Element | null
  private overlay: Element | null
  private readonly mobileAnimationTime: number = 300

  constructor() {
    this.body = document.body
    this.header = document.querySelector('#js-page-header')
    this.toggle = this.header?.querySelector('.js-mobile-toggle') || null
    this.navigation = document.querySelector('#js-navigation')
    this.menu = this.navigation?.querySelector('.menu') || null
    this.overlay = document.querySelector('#js-page-overlay')

    this.bindMobileEvents()
    this.bindDesktopEvents()
  }

  /**
   * Open the mobile menu
   */
  open(): void {
    if (!this.toggle) return
    
    const hamburger = this.toggle.querySelector('.js-hamburger')
    hamburger?.classList.add('active')
    this.navigation?.classList.add('active')
    this.overlay?.classList.add('active')
    this.body?.classList.add('scroll-locked')
    this.toggle.setAttribute('aria-expanded', 'true')
    this.toggle.setAttribute('aria-label', 'Close menu')
  }

  /**
   * Close the mobile menu
   */
  close(): void {
    if (!this.toggle) return
    
    const hamburger = this.toggle.querySelector('.js-hamburger')
    hamburger?.classList.remove('active')
    this.navigation?.classList.remove('active')
    this.body?.classList.remove('scroll-locked')
    this.overlay?.classList.remove('active')
    this.toggle.setAttribute('aria-expanded', 'false')
    this.toggle.setAttribute('aria-label', 'Open menu')

    // Remove active from overlay children
    if (!this.overlay) return
    const activeElements = this.overlay.querySelectorAll('.active')
    activeElements.forEach((el) => el.classList.remove('active'))
  }

  /**
   * Bind different events for mobile
   */
  bindMobileEvents(): void {
    // Toggle to open menu
    this.toggle?.addEventListener('click', () => {
      if (!this.navigation?.classList.contains('active')) {
        this.open()
      } else {
        this.close()
      }
    })

    // Close menu before opening a link
    this.menu?.addEventListener('click', (e) => {
      const target = e.target as HTMLElement
      if (target.tagName === 'A' && this.navigation?.classList.contains('active')) {
        e.preventDefault()
        const url = target.getAttribute('href')

        this.close()
        if (url) {
          setTimeout(() => {
            window.location.href = url
          }, this.mobileAnimationTime)
        }
      }
    })

    document.addEventListener('keyup', (e) => {
      if (e.key === 'Escape' && this.navigation?.classList.contains('active')) {
        this.close()
      }
    })
  }

  /**
   * Bind different events for desktop
   */
  bindDesktopEvents(): void {
    // Navigation
    this.navigation?.addEventListener('mouseenter', (e) => {
      const target = (e.target as Element).closest('.menu > li')
      if (target && (getMediaQuery() === 'lg' || getMediaQuery() === 'xl')) {
        target.classList.add('is-hovered')
      }
    }, true)

    this.navigation?.addEventListener('mouseleave', (e) => {
      const target = (e.target as Element).closest('.menu > li')
      if (target && (getMediaQuery() === 'lg' || getMediaQuery() === 'xl')) {
        target.classList.remove('is-hovered')
      }
    }, true)

    this.navigation?.addEventListener('touchstart', (e) => {
      const mq = getMediaQuery()
      if (mq !== 'lg' && mq !== 'xl') {
        return
      }

      const target = (e.target as Element).closest('.menu > li > a')
      if (target) {
        const menuItem = target.closest('li') as Element | null
        if (menuItem?.querySelector('.js-sub-menu-wrapper')) {
          e.preventDefault()
          const mouseEnter = new Event('mouseenter', { bubbles: true })
          menuItem.dispatchEvent(mouseEnter)
        }
      }
    }, true)

    // User Menu
    const userMenu = this.header?.querySelector('.js-user')
    if (userMenu) {
      userMenu.addEventListener('mouseenter', () => {
        if (getMediaQuery() === 'lg' || getMediaQuery() === 'xl') {
          userMenu.classList.add('is-hovered')
        }
      })

      userMenu.addEventListener('mouseleave', () => {
        if (getMediaQuery() === 'lg' || getMediaQuery() === 'xl') {
          userMenu.classList.remove('is-hovered')
        }
      })
    }

    // Close submenu on click outside
    document.addEventListener('click', (e) => {
      const mq = getMediaQuery()
      const target = e.target as Element

      if (
        (mq === 'lg' || mq === 'xl') &&
        !target.closest('.js-sub-menu-wrapper') &&
        this.navigation
      ) {
        const subMenus = this.navigation.querySelectorAll('.js-sub-menu-wrapper')
        subMenus.forEach((menu) => {
          if ((menu as HTMLElement).offsetParent !== null) {
            ;(menu as HTMLElement).style.display = ''
            menu.closest('.menu > li')?.classList.remove('is-hovered')
          }
        })
      }
    })
  }
}
